<template>
    <CqModal :showModal="showModal" @closeModal="closeModal" heading="Legenda" width="fit-content" maxWidth="50vw">
        <div id="legendContainer">
            <h2 class="legend-header">Faselegenda</h2>
            <div class="legenda-grid">
                <HeaderLegendItems
                    :globals="globals"
                    :viewOptions="viewOptions"
                />
            </div>

            <h2 class="legend-header">Daglegenda</h2>
            <div class="legenda-grid">
                <CellLegendItems
                    :globals="globals"
                    :deploymentStates="freeAndSickDeploymentStates"
                    :rosterNames="fakeRosterNames"
                    :permissions="permissions"
                />
            </div>

            <h2 class="legend-header">Dienstenlegenda</h2>
            <div id="deploymentLegend" class="legenda-grid">
                <DeploymentLegendItems
                    :deploymentStates="enabledDeploymentStates"
                    :rosterNames="fakeRosterNames"
                    :enabledFeatures="enabledFeatures"
                />
            </div>
        </div>
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {CqModal} from '@cquential/ui';
    import {Globals, OrganisationDeploymentState, Organisation, DeploymentState} from '@cquential/api';
    import {ViewOptions, PermissionsForRoster} from '@/types/types';
    import CellLegendItems from '@/components/modals/Legend/CellLegendItems.vue';
    import DeploymentLegendItems from '@/components/modals/Legend/DeploymentLegendItems.vue';
    import HeaderLegendItems from '@/components/modals/Legend/HeaderLegendItems.vue';

    @Component({
        components: {
            CqModal,
            CellLegendItems,
            DeploymentLegendItems,
            HeaderLegendItems,
        },
        emits: ['closeModal'],
    })
    export default class LegendModal extends Vue {
        @Prop({required: true}) public viewOptions!: ViewOptions;
        @Prop({required: true}) public globals!: Globals;
        @Prop({required: true}) public organisations!: Record<string, Organisation>;
        @Prop({required: true}) public permissions!: PermissionsForRoster;
        @Prop({required: true}) public enabledFeatures!: string[];
        @Prop({type: Boolean}) public showModal!: boolean;

        private static readonly fakeRosterNames: Record<number, string> = {
            1: 'Ander rooster',
            2: 'Huidig rooster',
        };

        public get fakeRosterNames(): Record<number, string> {
            return LegendModal.fakeRosterNames;
        }

        public get enabledDeploymentStates(): Record<DeploymentState, OrganisationDeploymentState[]> {
            const result: Record<DeploymentState, OrganisationDeploymentState[]> = {
                [DeploymentState.Maternity]: [],
                [DeploymentState.Normal]: [],
                [DeploymentState.Free]: [],
                [DeploymentState.Sick]: [],
                [DeploymentState.Extra]: [],
                [DeploymentState.SpecialLeave]: [],
                [DeploymentState.Waitday]: [],
                [DeploymentState.UnpaidLeave]: [],
                [DeploymentState.PartimeLeave]: [],
                [DeploymentState.CompensationLeave]: [],
                [DeploymentState.Training]: [],
                [DeploymentState.Leave2]: [],
                [DeploymentState.Extra2]: [],
                [DeploymentState.CareLeave]: [],
                [DeploymentState.SpecialLeave2]: [],
                [DeploymentState.LeaveDuringSickness]: [],
            };

            for (const organisation of Object.values(this.organisations)) {
                for (const organisationState of Object.values(organisation.organisationDeploymentStates)) {
                    if (!result[organisationState.id].some((state: OrganisationDeploymentState): boolean => state.comment === organisationState.comment)) {
                        result[organisationState.id].push(organisationState);
                    }
                }
            }

            return result;
        }

        public get freeAndSickDeploymentStates(): Record<number, OrganisationDeploymentState> {
            const result: Record<number, OrganisationDeploymentState> = {};
            for (const organisation of Object.values(this.organisations)) {
                for (const organisationState of Object.values(organisation.organisationDeploymentStates)) {
                    const state: OrganisationDeploymentState | undefined = result[organisationState.deploymentStateId];
                    if (!state && (organisationState.id === DeploymentState.Free || organisationState.id === DeploymentState.Sick)) {
                        result[organisationState.deploymentStateId] = {...organisationState};
                    }
                }
            }

            return result;
        }

        public closeModal(): void {
            this.$emit('closeModal');
        }
    }
</script>

<style lang="scss" scoped>
    #legendContainer {
        pointer-events: none;

        .legend-header {
            margin-top: 2rem;
            padding-bottom: 0.5rem;
            border-bottom: solid 1px var(--deployment-marker-border-top-color);
            margin-bottom: 0;
        }

        .legenda-grid {
            display: grid;
            grid-template-columns: minmax(6rem, 9rem) 1fr;
        }
    }
</style>
